<template>
    <v-layout justify-center>
        <v-responsive width="100%" height="100%" max-width="1080" class="mx-n3 px-3">
            <list-heading title="자재연결 관리">
                <template #buttons>
                    <manufacturing-material-list-sync v-on="{ search }">
                        <template #activator="{ attrs, on }">
                            <v-btn small color="primary" v-bind="attrs" v-on="on">
                                <v-icon small class="mr-1">mdi-sync</v-icon>
                                <span>MES 데이터 싱크</span>
                            </v-btn>
                        </template>
                    </manufacturing-material-list-sync>
                </template>
            </list-heading>

            <manufacturing-material-list-search v-bind="{ loading }" />

            <v-data-table v-bind="{ loading, headers, items }" disable-sort disable-pagination hide-default-footer :items-per-page="-1" class="v-sheet--outlined mt-4" @click:row="onClickRow">
                <template v-for="(header, index) in headers.filter((header) => header.hasOwnProperty('formatter'))" #[`item.${header.value}`]="{ value, item }"> <span :key="index" :title="header.withTitle ? header.formatter.bind(item)(value, item) : undefined" v-html="header.formatter.bind(item)(value, item)" /> </template>

                <template #[`item.code`]="{ item }">
                    <v-list-item dense target="_blank" :href="item.href" @click.stop>
                        <v-list-item-avatar color="content">
                            <v-img :src="item.photo">
                                <template #placeholder> <v-icon>mdi-chip</v-icon> </template>
                            </v-img>
                        </v-list-item-avatar>
                        <v-list-item-content>
                            <v-list-item-title :title="item.code"> {{ item.code }} </v-list-item-title>
                        </v-list-item-content>
                    </v-list-item>
                </template>

                <template #[`item.shows`]="{ item }">
                    <v-switch v-model="item.shows" class="d-inline-block ml-1 mr-n3" v-bind="attrs_switch" @click.stop.capture @change="(shows) => update({ _id: item._id, shows })" />
                </template>
            </v-data-table>

            <v-pagination :value="page" :length="pageCount" :total-visible="11" class="mt-3 mx-4" @input="(page) => $router.push({ query: { ...$route.query, page } })"></v-pagination>
        </v-responsive>
    </v-layout>
</template>

<script>
import api from "@/api";
import { initDataTableHeaders, attrs_switch } from "@/assets/variables";

import ListHeading from "@/components/console/dumb/list-heading.vue";
import ManufacturingMaterialListSearch from "@/components/console/manufacturing/material/list/manufacturing-material-list-search.vue";
import ManufacturingMaterialListSync from "@/components/console/manufacturing/material/list/manufacturing-material-list-sync.vue";

const headers = initDataTableHeaders([
    { text: "#", value: "index", width: +80, align: "right" },
    { text: "분류", value: "category.name", cellClass: "text-truncate max-width-0", withTitle: true, width: 160 },
    { text: "자재", value: "code", cellClass: "text-truncate max-width-0 pa-0", withTitle: true },
    { text: "설명", value: "name", cellClass: "text-truncate max-width-0", withTitle: true },
    { text: "제조사", value: "maker", cellClass: "text-truncate max-width-0", withTitle: true, width: 160 },
    { text: "표시여부", value: "shows", width: +80 },
    { text: "자재연결", value: "meta.summary_link.totalCount", formatter: (totalCount = 0) => `${totalCount}건`, width: +80 },
]);

export default {
    components: {
        ListHeading,
        ManufacturingMaterialListSearch,
        ManufacturingMaterialListSync,
    },
    data: () => ({
        materials: [],

        limit: 10,
        summary: { totalCount: 0 },

        loading: false,
        headers,
        attrs_switch,
    }),
    computed: {
        items() {
            return this.materials.map((item, index) => ({ ...item, index: this.summary.totalCount - (this.page - 1) * this.limit - index }));
        },
        page() {
            return +(this.$route.query.page || "1");
        },
        skip() {
            return (this.page - 1) * this.limit;
        },
        pageCount() {
            return Math.ceil(this.summary.totalCount / this.limit) || 1;
        },
        params() {
            let { query } = this.$route;
            return { ...query };
        },
    },
    mounted() {
        this.init();
    },
    watch: {
        params() {
            this.search();
        },
    },
    methods: {
        async init() {
            try {
                this.search();
            } catch (error) {
                console.error(error);
            }
        },

        async search() {
            this.loading = true;
            try {
                let { skip, limit, params } = this;
                var { summary, materials } = await api.console.manufacturing.materials.gets({
                    headers: { skip, limit },
                    params,
                });

                this.materials = materials;
                this.summary = summary;
            } finally {
                this.loading = false;
            }
        },

        async update(item) {
            if (this.loading) return;
            else this.loading = true;

            try {
                const { material } = await api.console.manufacturing.materials.put(item);
                this.loading = false;
                this.updateItem(material);
            } finally {
                if (this.loading) this.loading = false;
            }
        },

        updateItem(item) {
            const index = this.materials.findIndex(({ _id }) => _id == item._id);
            if (0 <= index) this.materials.splice(index, 1, item);
            else {
                this.summary.totalCount += 1;
                this.materials = [item, ...this.materials];
            }
            this.search();
        },

        onClickRow({ _id }) {
            this.$router.push(`${this.$route.path}/${_id}`);
        },
    },
};
</script>

<style lang="scss" scoped>
::v-deep {
    .line-height-1-5 {
        line-height: 1.5;
    }
    .max-width-0 {
        max-width: 0;
    }
    .word-break-keep-all {
        word-break: keep-all;
    }
    .white-space-pre-line {
        white-space: pre-line;
    }
    .cursor-pointer {
        cursor: pointer;
    }
    .v-pagination button {
        box-shadow: none !important;
        border: thin solid rgba(0, 0, 0, 0.12);
    }
    .v-small-dialog__activator {
        height: 100%;
    }
    .v-small-dialog__activator__content {
        width: 100%;
    }

    th:first-of-type:not(:last-of-type),
    td:first-of-type:not(:last-of-type) {
        border-right: thin solid rgba(0, 0, 0, 0.12);
    }

    .v-data-table__expanded__content {
        box-shadow: none !important;
    }
    .vertical-align-top {
        vertical-align: top !important;
    }

    .v-data-table tr td {
        cursor: pointer !important;
    }
}
</style>
